/**
 * GENERATION MODEL PRICING CONFIGURATION
 *
 * Pricing for image and video generation models
 * Separate from text generation LLM pricing (shared-pricing-config.ts)
 *
 * Architecture:
 * - Nested by base model → generation type → variant config
 * - 'family': The actual model creator/provider (google, bytedance, kling, wan, grok, openai)
 * - ALL PARAMS USE CANONICAL NAMES (from shared-generation-params.ts)
 * - Backend providers map canonical names to API-specific names via PARAM_MAPPINGS
 *
 * Kie is a 3rd-party API aggregator that provides access to Google, Bytedance, Kling, Wan, and Grok models.
 * ModelStack is the 4th layer - we proxy through Kie to reach these upstream providers.
 *
 * Pricing verified from official Kie API documentation: https://docs.kie.ai
 */

import type { CanonicalParam } from './shared-generation-params'

export type GenerationModelFamily = 'google' | 'bytedance' | 'kling' | 'wan' | 'grok' | 'openai' | 'elevenlabs' | 'replicate' | 'stability' | 'unknown'

/**
 * Generation operation types - matches task-worker domain
 */
export type GenerationType =
  // Image Generation
  | 'TEXT_TO_IMAGE'
  | 'IMAGE_TO_IMAGE'
  | 'IMAGE_TO_IMAGE_EDIT'
  | 'IMAGE_TO_IMAGE_UPSCALE'
  | 'TEXT_IMAGE_TO_IMAGE'
  // Video Generation
  | 'TEXT_TO_VIDEO'
  | 'IMAGE_TO_VIDEO'
  | 'IMAGE_TO_VIDEO_ANIMATE'
  | 'TEXT_IMAGE_TO_VIDEO'
  | 'VIDEO_TO_VIDEO'
  | 'VIDEO_TO_VIDEO_EDIT'
  | 'VIDEO_TO_VIDEO_EXTEND'
  | 'VIDEO_TO_VIDEO_UPSCALE'
  | 'TEXT_VIDEO_TO_VIDEO'
  // Audio/Speech to Video
  | 'SPEECH_TO_VIDEO'
  | 'AUDIO_TO_VIDEO'
  // Audio Generation
  | 'TEXT_TO_AUDIO'
  // Reference-based
  | 'REFERENCE_TO_VIDEO'
  | 'REFERENCE_TO_IMAGE'
  // Motion Control
  | 'VIDEO_MOTION_CONTROL'
  | 'IMAGE_MOTION_CONTROL'

/**
 * Generation category - high-level grouping for UI filtering
 */
export type GenerationCategory = 'image' | 'video' | 'audio'

/**
 * Map generation types to their category
 */
export const GENERATION_TYPE_TO_CATEGORY: Record<GenerationType, GenerationCategory> = {
  // Image types
  TEXT_TO_IMAGE: 'image', IMAGE_TO_IMAGE: 'image', IMAGE_TO_IMAGE_EDIT: 'image',
  IMAGE_TO_IMAGE_UPSCALE: 'image', TEXT_IMAGE_TO_IMAGE: 'image',
  REFERENCE_TO_IMAGE: 'image', IMAGE_MOTION_CONTROL: 'image',
  // Video types
  TEXT_TO_VIDEO: 'video', IMAGE_TO_VIDEO: 'video', IMAGE_TO_VIDEO_ANIMATE: 'video',
  TEXT_IMAGE_TO_VIDEO: 'video', VIDEO_TO_VIDEO: 'video', VIDEO_TO_VIDEO_EDIT: 'video',
  VIDEO_TO_VIDEO_EXTEND: 'video', VIDEO_TO_VIDEO_UPSCALE: 'video',
  TEXT_VIDEO_TO_VIDEO: 'video', REFERENCE_TO_VIDEO: 'video', VIDEO_MOTION_CONTROL: 'video',
  // Audio types
  SPEECH_TO_VIDEO: 'audio', AUDIO_TO_VIDEO: 'audio', TEXT_TO_AUDIO: 'audio',
}

export function getGenerationTypesByCategory(category: GenerationCategory): GenerationType[] {
  return Object.entries(GENERATION_TYPE_TO_CATEGORY)
    .filter(([_, cat]) => cat === category)
    .map(([type]) => type as GenerationType)
}

// ========================================
// PRICING TYPES
// ========================================

export interface ParamPriceTiers {
  [paramValue: string]: number  // Price in millidollars for this param value
}

/**
 * Per-second pricing for video variants where duration is a user-selectable param
 * and cost scales linearly with it (verified against Kie's real per-second rates).
 * `ratesPerSecond` is keyed by the values of `tierParams` joined with '|' (in order).
 */
export interface DurationTieredPricing {
  /** Discriminant so this shape can be distinguished from Record<string, ParamPriceTiers> */
  kind: 'duration'
  /** Canonical params (besides duration) whose values combine to select a rate */
  tierParams: CanonicalParam[]
  /** Millidollars per second, keyed by tierParams values joined with '|' */
  ratesPerSecond: Record<string, number>
  /** Assumed duration (seconds) when settings.duration is missing */
  defaultDurationSeconds: number
  /** Default value per tierParam, matching what the provider actually defaults to when that param is missing */
  defaultValues: Record<string, string>
}

export type PriceUnit =
  | 'per-image'
  | 'per-video'
  | 'per-second-video'
  | 'per-second-audio'
  | 'per-1k-characters'
  | 'per-request'

/**
 * Per-character pricing for text-to-speech variants, where cost scales with the length
 * of the generation prompt (the text to be spoken) rather than a request-time param like
 * duration or resolution. Unlike video duration, audio length isn't user-selectable — it's
 * a function of how much text they typed, so we bill against `settings.prompt.length`.
 */
export interface TextLengthTieredPricing {
  /** Discriminant so this shape can be distinguished from Record<string, ParamPriceTiers> */
  kind: 'text-length'
  /** Millidollars per 1000 characters of prompt text */
  ratePer1kChars: number
}

/** Configuration for a specific generation type variant */
export interface VariantConfig {
  /** Flat price (number), tiered pricing by param values, per-second duration-scaled pricing, or per-character pricing */
  pricing: number | Record<string, ParamPriceTiers> | DurationTieredPricing | TextLengthTieredPricing
  /** Canonical params this variant supports */
  supportedParams: CanonicalParam[]
  /** Description shown in UI */
  description: string
  /** Featured in UI */
  featured?: boolean
  /** Override label in Generation Type selector */
  generationDisplayName?: string
  /** Override price unit (defaults to base model's priceUnit) */
  priceUnit?: PriceUnit
  /**
   * Restricts the duration selector to specific values (e.g. Kling 2.6 only accepts "5" or
   * "10" seconds, not a continuous range) when the provider enforces a discrete enum rather
   * than an arbitrary length. Omit for models with a continuous duration range — the UI falls
   * back to the generic duration list, which fits within known continuous ranges today.
   */
  allowedDurations?: string[]
}

/** Configuration for a base model (e.g., "google/nano-banana-2") */
export interface BaseModelConfig {
  displayName: string
  family: GenerationModelFamily
  priceUnit: PriceUnit
  variants: Partial<Record<GenerationType, VariantConfig>>
}

// ========================================
// MODEL CATALOG
// ========================================
//
// Generation pricing is flat and prepaid-only — there is no per-tier markup like
// shared-pricing-config.ts's chat/LLM tier multiplier resolution.
// A GENERATION_TIER_MULTIPLIERS table previously existed here, fully populated but never
// imported anywhere (verified via repo-wide grep) — removed 2026-08-26 rather than left as
// misleading dead config implying tiered generation pricing exists. If tiered generation
// pricing becomes a real product requirement, design it fresh against computeGenerationPrice()
// and MediaGenerationBillingAdapter rather than reviving this table.

export const GENERATION_MODEL_CATALOG: Record<string, BaseModelConfig> = {
  // ========================================
  // GOOGLE MODELS
  // ========================================

  'google/nano-banana-2': {
    displayName: 'Nano Banana 2',
    family: 'google',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        pricing: { resolution: { '1K': 80, '2K': 120, '4K': 150 } },
        supportedParams: ['prompt', 'aspect_ratio', 'resolution', 'output_format'],
        description: 'Google Nano Banana 2 text-to-image generation. High-quality image generation with flexible aspect ratios.',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        pricing: { resolution: { '1K': 80, '2K': 120, '4K': 150 } },
        supportedParams: ['prompt', 'image_input', 'aspect_ratio', 'resolution', 'output_format'],
        description: 'Google Nano Banana 2 image-to-image generation. Supports up to 14 reference images.',
        featured: false,
      },
    },
  },

  'google/nano-banana-2-lite': {
    displayName: 'Nano Banana 2 Lite',
    family: 'google',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        pricing: 40,
        supportedParams: ['prompt', 'aspect_ratio'],
        description: 'Google Nano Banana 2 Lite text-to-image. Fast, efficient generation.',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        pricing: 40,
        supportedParams: ['prompt', 'image_urls', 'aspect_ratio'],
        description: 'Google Nano Banana 2 Lite image-to-image. Up to 10 reference images.',
        featured: false,
      },
    },
  },

  'google/nano-banana-pro': {
    displayName: 'Nano Banana Pro',
    family: 'google',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        pricing: { resolution: { '1K': 150, '2K': 150, '4K': 200 } },
        supportedParams: ['prompt', 'aspect_ratio', 'resolution', 'output_format'],
        description: 'Google Nano Banana Pro text-to-image - enhanced quality and detail.',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        pricing: { resolution: { '1K': 150, '2K': 150, '4K': 200 } },
        supportedParams: ['prompt', 'image_input', 'aspect_ratio', 'resolution', 'output_format'],
        description: 'Google Nano Banana Pro image-to-image - professional grade with up to 8 reference images.',
        featured: false,
      },
    },
  },

  // ========================================
  // OPENAI MODELS
  // ========================================

  'openai/gpt-image-2': {
    displayName: 'GPT Image 2',
    family: 'openai',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        pricing: { resolution: { '1K': 50, '2K': 80, '4K': 120 } },
        supportedParams: ['prompt', 'aspect_ratio', 'resolution'],
        description: 'OpenAI GPT Image 2 text-to-image generation via Kie AI.',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        pricing: { resolution: { '1K': 50, '2K': 80, '4K': 120 } },
        supportedParams: ['prompt', 'input_urls', 'aspect_ratio', 'resolution'],
        description: 'OpenAI GPT Image 2 image-to-image transformation via Kie AI.',
        featured: false,
      },
    },
  },

  'openai/gpt-image-1.5': {
    displayName: 'GPT Image 1.5',
    family: 'openai',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        // Kie's schema only enumerates medium/high — verified against
        // docs.kie.ai/market/gpt-image/1-5-text-to-image.md ("low" is not a valid value)
        pricing: { quality: { medium: 30, high: 140 } },
        supportedParams: ['prompt', 'aspect_ratio', 'quality'],
        description: 'OpenAI GPT Image 1.5 text-to-image generation via Kie AI.',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        // Kie's schema only enumerates medium/high — verified against
        // docs.kie.ai/market/gpt-image/1-5-text-to-image.md ("low" is not a valid value)
        pricing: { quality: { medium: 30, high: 140 } },
        supportedParams: ['prompt', 'input_urls', 'aspect_ratio', 'quality'],
        description: 'OpenAI GPT Image 1.5 image-to-image generation via Kie AI.',
        featured: false,
      },
    },
  },

  // ========================================
  // GOOGLE VEO 3
  // ========================================

  'google/veo3': {
    displayName: 'Veo 3',
    family: 'google',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: 10000,
        supportedParams: ['prompt', 'aspect_ratio', 'duration', 'resolution'],
        description: 'Google Veo 3 text-to-video (quality mode). Up to 8 seconds.',
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: 10000,
        supportedParams: ['prompt', 'image_inputs', 'aspect_ratio', 'duration', 'resolution'],
        description: 'Google Veo 3 image-to-video (quality mode). Up to 8 seconds.',
        featured: false,
      },
    },
  },

  'google/veo3-fast': {
    displayName: 'Veo 3 Fast',
    family: 'google',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: 7000,
        supportedParams: ['prompt', 'aspect_ratio', 'duration', 'resolution'],
        description: 'Google Veo 3 Fast text-to-video (speed mode). Up to 8 seconds.',
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: 7000,
        supportedParams: ['prompt', 'image_inputs', 'aspect_ratio', 'duration', 'resolution'],
        description: 'Google Veo 3 Fast image-to-video (speed mode). Up to 8 seconds.',
        featured: false,
      },
    },
  },

  'google/veo3-lite': {
    displayName: 'Veo 3 Lite',
    family: 'google',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: 5000,
        supportedParams: ['prompt', 'aspect_ratio', 'duration', 'resolution'],
        description: 'Google Veo 3 Lite text-to-video (efficiency mode). Up to 8 seconds.',
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: 5000,
        supportedParams: ['prompt', 'image_inputs', 'aspect_ratio', 'duration', 'resolution'],
        description: 'Google Veo 3 Lite image-to-video (efficiency mode). Up to 8 seconds.',
        featured: false,
      },
    },
  },

  // ========================================
  // KLING MODELS
  // ========================================

  'kling/2.6': {
    displayName: 'Kling 2.6',
    family: 'kling',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['sound'],
          ratesPerSecond: { false: 165, true: 330 },
          defaultDurationSeconds: 5,
          defaultValues: { sound: 'false' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'sound', 'aspect_ratio', 'duration'],
        // Verified against docs.kie.ai/market/kling/text-to-video.md: duration only accepts
        // the discrete values "5" or "10", not a continuous range.
        allowedDurations: ['5', '10'],
        description: 'Kling 2.6 text-to-video generation. Up to 10 seconds with audio support.',
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['sound'],
          ratesPerSecond: { false: 165, true: 330 },
          defaultDurationSeconds: 5,
          defaultValues: { sound: 'false' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'image_urls', 'sound', 'duration'],
        allowedDurations: ['5', '10'],
        description: 'Kling 2.6 image-to-video generation. Animates still images with a text prompt.',
        featured: false,
      },
      VIDEO_MOTION_CONTROL: {
        // Was a flat 12000 (guessed — 2026-08-25 audit flagged this as "not published" /
        // unverified). Kie actually bills this per-second: kie.ai/pricing confirms
        // 1080p=$0.09/s, 720p=$0.055/s (2026-08-26). Applying the same 3x margin already
        // established for this family's T2V/I2V (real $0.055/s -> our $0.165/s there).
        // No duration param exists here — output length tracks the uploaded reference
        // video (3-30s per docs.kie.ai/market/kling/motion-control.md), unknown at
        // request time, so we bill at Kie's documented max (30s) to never undercharge.
        pricing: {
          kind: 'duration',
          tierParams: ['mode'],
          ratesPerSecond: { '1080p': 270, '720p': 165 },
          defaultDurationSeconds: 30,
          defaultValues: { mode: '720p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'input_urls', 'video_urls', 'character_orientation', 'mode'],
        description: 'Kling 2.6 motion-controlled video generation.',
        featured: false,
      },
    },
  },

  'kling/3.0': {
    displayName: 'Kling 3.0',
    family: 'kling',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['mode', 'sound'],
          ratesPerSecond: {
            'std|false': 210,
            'std|true': 300,
            'pro|false': 270,
            'pro|true': 405,
            '4k|false': 1005,
            '4k|true': 1005,
          },
          defaultDurationSeconds: 5,
          defaultValues: { mode: 'std', sound: 'false' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'sound', 'aspect_ratio', 'duration', 'mode'],
        description: 'Kling 3.0 video generation. Latest model with selectable resolution (std/pro/4K).',
        featured: true,
      },
      // Same Kie endpoint as TEXT_TO_VIDEO (kling-3.0/video) — accepts image_urls for
      // image-conditioned generation at the same per-second rate. Verified 2026-08-26
      // against docs.kie.ai/market/kling/kling-3-0.md; was previously missing from the
      // catalog even though the provider code already routed IMAGE_TO_VIDEO for kling/3.0.
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['mode', 'sound'],
          ratesPerSecond: {
            'std|false': 210,
            'std|true': 300,
            'pro|false': 270,
            'pro|true': 405,
            '4k|false': 1005,
            '4k|true': 1005,
          },
          defaultDurationSeconds: 5,
          defaultValues: { mode: 'std', sound: 'false' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'image_urls', 'sound', 'aspect_ratio', 'duration', 'mode'],
        description: 'Kling 3.0 image-to-video generation. Animates a reference image with selectable resolution (std/pro/4K).',
        featured: false,
      },
      VIDEO_MOTION_CONTROL: {
        // Was a flat 13000 (guessed — 2026-08-25 audit flagged this as "not published" /
        // unverified). Kie actually bills this per-second: kie.ai/pricing confirms
        // 1080p=$0.135/s, 720p=$0.10/s (2026-08-26). Applying the same 3x margin already
        // established for this family's T2V/I2V (real $0.07/s -> our $0.21/s there).
        // No duration param exists here — output length tracks the uploaded reference
        // video (3-30s per docs.kie.ai/market/kling/motion-control-v3.md), unknown at
        // request time, so we bill at Kie's documented max (30s) to never undercharge.
        pricing: {
          kind: 'duration',
          tierParams: ['mode'],
          ratesPerSecond: { '1080p': 405, '720p': 300 },
          defaultDurationSeconds: 30,
          defaultValues: { mode: '720p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'input_urls', 'video_urls', 'character_orientation', 'mode'],
        description: 'Kling 3.0 with motion control.',
        featured: false,
      },
    },
  },

  'kling/3.0-turbo': {
    displayName: 'Kling 3.0 Turbo',
    family: 'kling',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '720p': 270, '1080p': 338 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '720p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'aspect_ratio', 'duration', 'resolution'],
        description: 'Kling 3.0 Turbo text-to-video. Fast processing with latest model.',
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '720p': 270, '1080p': 338 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '720p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'image_url', 'aspect_ratio', 'duration', 'resolution'],
        description: 'Kling 3.0 Turbo image-to-video. Fast image animation with latest model.',
        featured: false,
      },
    },
  },

  'kling/ai-avatar': {
    displayName: 'Kling Avatar',
    family: 'kling',
    priceUnit: 'per-video',
    variants: {
      IMAGE_TO_VIDEO: {
        pricing: 6000,
        supportedParams: ['prompt', 'image_url', 'audio_input'],
        description: 'Kling AI Avatar Standard. Generate talking avatars from images with audio.',
        featured: false,
      },
      SPEECH_TO_VIDEO: {
        pricing: 6000,
        supportedParams: ['prompt', 'image_url', 'audio_input'],
        description: 'Kling AI Avatar Standard. Generate talking avatars from audio.',
        featured: false,
      },
    },
  },

  'kling/ai-avatar-pro': {
    displayName: 'Kling Avatar Pro',
    family: 'kling',
    priceUnit: 'per-video',
    variants: {
      IMAGE_TO_VIDEO: {
        pricing: 9000,
        supportedParams: ['prompt', 'image_url', 'audio_input'],
        description: 'Kling AI Avatar Pro. Premium quality talking avatars with enhanced realism.',
        featured: false,
      },
      SPEECH_TO_VIDEO: {
        pricing: 9000,
        supportedParams: ['prompt', 'image_url', 'audio_input'],
        description: 'Kling AI Avatar Pro. Premium quality talking avatars from audio.',
        featured: false,
      },
    },
  },

  // ========================================
  // WAN MODELS
  // ========================================

  'wan/2-7-image': {
    displayName: 'Wan 2.7 Image',
    family: 'wan',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        // Was 3500 (~146x Kie's real $0.024/image) — corrected 2026-08-26 to Kie cost ($0.024,
        // verified live on kie.ai/pricing) × 3, matching Wan's dominant catalog-wide margin.
        pricing: 72,
        supportedParams: ['prompt', 'input_urls', 'aspect_ratio', 'enable_sequential', 'num_images', 'resolution', 'thinking_mode', 'watermark', 'seed', 'nsfw_checker'],
        description: 'Wan 2.7 Image text-to-image generation. Supports multi-image sequential mode, color palettes, and bounding boxes.',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        // Was 4000 — Kie prices this identically to T2I ($0.024/image, no I2I premium), so no
        // synthetic differential was invented; corrected to the same 3x-marked-up value.
        pricing: 72,
        supportedParams: ['prompt', 'input_urls', 'aspect_ratio', 'enable_sequential', 'num_images', 'resolution', 'watermark', 'seed', 'nsfw_checker'],
        description: 'Wan 2.7 Image image-to-image generation with up to 9 reference images.',
        featured: false,
      },
    },
  },

  'wan/2-7-image-pro': {
    displayName: 'Wan 2.7 Image Pro',
    family: 'wan',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        // Was 4500 (~75x Kie's real $0.06/image) — corrected 2026-08-26 to Kie cost × 3.
        pricing: 180,
        supportedParams: ['prompt', 'input_urls', 'aspect_ratio', 'enable_sequential', 'num_images', 'resolution', 'thinking_mode', 'watermark', 'seed', 'nsfw_checker'],
        description: 'Wan 2.7 Image Pro text-to-image generation (enhanced quality).',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        // Was 5000 — Kie prices T2I/I2I identically, corrected to the same 3x-marked-up value.
        pricing: 180,
        supportedParams: ['prompt', 'input_urls', 'aspect_ratio', 'enable_sequential', 'num_images', 'resolution', 'watermark', 'seed', 'nsfw_checker'],
        description: 'Wan 2.7 Image Pro image-to-image generation (enhanced quality).',
        featured: false,
      },
    },
  },

  'wan/2.2-turbo': {
    displayName: 'Wan 2.2 Turbo',
    family: 'wan',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: 5500,
        // No 'duration' field exists upstream (fixed-length output) — verified against
        // docs.kie.ai/market/wan/2-2-a14b-text-to-video-turbo.md
        supportedParams: ['prompt', 'aspect_ratio', 'resolution', 'seed', 'nsfw_checker'],
        description: 'Wan 2.2 Turbo text-to-video generation (fast processing).',
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: 6000,
        // No 'duration'/'aspect_ratio' upstream — verified against
        // docs.kie.ai/market/wan/2-2-a14b-image-to-video-turbo.md
        supportedParams: ['prompt', 'image_input', 'resolution', 'seed', 'nsfw_checker'],
        description: 'Wan 2.2 Turbo image-to-video generation (fast processing).',
        featured: false,
      },
      // SPEECH_TO_VIDEO/AUDIO_TO_VIDEO: 'duration' confirmed not to exist upstream either
      // (docs.kie.ai/market/wan/2-2-a14b-speech-to-video-turbo.md uses num_frames/
      // frames_per_second/num_inference_steps/guidance_scale/shift instead — a materially
      // different param surface not yet modeled by our canonical params or provider code;
      // removing the false 'duration' claim here, full param-surface rewrite is a follow-up).
      SPEECH_TO_VIDEO: {
        pricing: 7000,
        supportedParams: ['audio_input', 'image_input', 'aspect_ratio'],
        description: 'Wan 2.2 Turbo speech-to-video generation.',
        featured: false,
      },
      AUDIO_TO_VIDEO: {
        pricing: 7000,
        supportedParams: ['audio_input', 'image_input', 'aspect_ratio'],
        description: 'Wan 2.2 Turbo audio-to-video generation.',
        featured: false,
      },
    },
  },

  'wan/2.2-animate': {
    displayName: 'Wan 2.2 Animate',
    family: 'wan',
    priceUnit: 'per-video',
    variants: {
      IMAGE_TO_VIDEO_ANIMATE: {
        pricing: 6500,
        supportedParams: ['video_input', 'image_input', 'resolution', 'nsfw_checker'],
        description: 'Wan 2.2 Animate (move mode). Replace elements in video using a reference image.',
        featured: false,
      },
    },
  },

  'wan/2.5': {
    displayName: 'Wan 2.5',
    family: 'wan',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: [],
          ratesPerSecond: { '': 180 },
          defaultDurationSeconds: 5,
          defaultValues: {  },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'aspect_ratio', 'duration'],
        description: 'Wan 2.5 text-to-video generation.',
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: [],
          ratesPerSecond: { '': 180 },
          defaultDurationSeconds: 5,
          defaultValues: {  },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'image_input', 'aspect_ratio', 'duration'],
        description: 'Wan 2.5 image-to-video generation.',
        featured: false,
      },
    },
  },

  'wan/2.6': {
    displayName: 'Wan 2.6',
    family: 'wan',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '720p': 210, '1080p': 314 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '1080p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'duration', 'resolution', 'nsfw_checker'],
        description: 'Wan 2.6 text-to-video generation.',
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: [],
          ratesPerSecond: { '': 210 },
          defaultDurationSeconds: 5,
          defaultValues: {  },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'image_urls', 'resolution', 'duration', 'nsfw_checker'],
        description: 'Wan 2.6 image-to-video generation.',
        featured: false,
      },
      VIDEO_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: [],
          ratesPerSecond: { '': 210 },
          defaultDurationSeconds: 5,
          defaultValues: {  },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'video_input', 'aspect_ratio', 'duration'],
        description: 'Wan 2.6 video-to-video transformation.',
        featured: false,
      },
    },
  },

  'wan/2.6-flash': {
    displayName: 'Wan 2.6 Flash',
    family: 'wan',
    priceUnit: 'per-video',
    variants: {
      IMAGE_TO_VIDEO: {
        pricing: 6500,
        supportedParams: ['prompt', 'image_input', 'aspect_ratio', 'duration'],
        description: 'Wan 2.6 Flash image-to-video (ultra-fast processing).',
        featured: false,
      },
      VIDEO_TO_VIDEO: {
        pricing: 7500,
        supportedParams: ['prompt', 'video_input', 'aspect_ratio', 'duration'],
        description: 'Wan 2.6 Flash video-to-video (ultra-fast processing).',
        featured: false,
      },
    },
  },

  'wan/2.7': {
    displayName: 'Wan 2.7',
    family: 'wan',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '720p': 240, '1080p': 360 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '1080p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'negative_prompt', 'resolution', 'ratio', 'duration', 'prompt_extend', 'watermark', 'audio_input', 'seed', 'nsfw_checker'],
        description: 'Wan 2.7 text-to-video generation. Up to 15 seconds, premium quality.',
        featured: true,
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '720p': 240, '1080p': 360 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '1080p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'first_frame_url', 'last_frame_url', 'negative_prompt', 'resolution', 'duration', 'prompt_extend', 'watermark', 'audio_input', 'seed', 'nsfw_checker'],
        description: 'Wan 2.7 image-to-video generation. Up to 15 seconds, premium quality.',
        featured: true,
      },
      VIDEO_TO_VIDEO_EDIT: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '720p': 240, '1080p': 360 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '1080p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'video_input', 'negative_prompt', 'resolution', 'ratio', 'duration', 'prompt_extend', 'watermark', 'seed', 'nsfw_checker'],
        description: 'Wan 2.7 video editing. Up to 15 seconds, advanced editing capabilities.',
        featured: false,
      },
      REFERENCE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '720p': 240, '1080p': 360 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '1080p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_images', 'negative_prompt', 'resolution', 'ratio', 'duration', 'prompt_extend', 'watermark', 'seed', 'nsfw_checker'],
        description: 'Wan 2.7 reference-based video generation. Multiple reference images supported.',
        featured: false,
      },
    },
  },

  // ========================================
  // BYTEDANCE SEEDANCE
  // ========================================

  'bytedance/seedance-2': {
    displayName: 'Seedance 2.0',
    family: 'bytedance',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 285, '720p': 615, '1080p': 1530, '4K': 3120 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 text-to-video.',
        featured: true,
        generationDisplayName: 'Text to Video',
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 285, '720p': 615, '1080p': 1530, '4K': 3120 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_image_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 image-to-video.',
        featured: false,
        generationDisplayName: 'Image to Video',
      },
      VIDEO_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 171, '720p': 375, '1080p': 930, '4K': 1920 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_video_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 video-to-video.',
        featured: false,
        generationDisplayName: 'Video to Video',
      },
      AUDIO_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 285, '720p': 615, '1080p': 1530, '4K': 3120 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_audio_urls', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 audio-to-video.',
        featured: false,
        generationDisplayName: 'Audio to Video',
      },
      REFERENCE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 285, '720p': 615, '1080p': 1530, '4K': 3120 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_image_urls', 'reference_video_urls', 'reference_audio_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 multi-modal.',
        featured: false,
        generationDisplayName: 'Reference to Video',
      },
    },
  },

  'bytedance/seedance-2-fast': {
    displayName: 'Seedance 2.0 Fast',
    family: 'bytedance',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 233, '720p': 495 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Fast text-to-video.',
        featured: false,
        generationDisplayName: 'Text to Video',
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 233, '720p': 495 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_image_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Fast image-to-video.',
        featured: false,
        generationDisplayName: 'Image to Video',
      },
      VIDEO_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 135, '720p': 300 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_video_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Fast video-to-video.',
        featured: false,
        generationDisplayName: 'Video to Video',
      },
      AUDIO_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 233, '720p': 495 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_audio_urls', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Fast audio-to-video.',
        featured: false,
        generationDisplayName: 'Audio to Video',
      },
      REFERENCE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 233, '720p': 495 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_image_urls', 'reference_video_urls', 'reference_audio_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Fast multi-modal.',
        featured: false,
        generationDisplayName: 'Reference to Video',
      },
    },
  },

  'bytedance/seedance-2-mini': {
    displayName: 'Seedance 2.0 Mini',
    family: 'bytedance',
    priceUnit: 'per-video',
    variants: {
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 143, '720p': 308 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Mini text-to-video.',
        featured: false,
        generationDisplayName: 'Text to Video',
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 143, '720p': 308 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_image_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Mini image-to-video.',
        featured: false,
        generationDisplayName: 'Image to Video',
      },
      VIDEO_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 90, '720p': 188 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_video_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Mini video-to-video.',
        featured: false,
        generationDisplayName: 'Video to Video',
      },
      AUDIO_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 143, '720p': 308 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_audio_urls', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Mini audio-to-video.',
        featured: false,
        generationDisplayName: 'Audio to Video',
      },
      REFERENCE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 143, '720p': 308 },
          defaultDurationSeconds: 5,
          defaultValues: { resolution: '480p' },
        },
        priceUnit: 'per-second-video',
        supportedParams: ['prompt', 'reference_image_urls', 'reference_video_urls', 'reference_audio_urls', 'generate_audio', 'resolution', 'aspect_ratio', 'duration', 'web_search', 'nsfw_checker'],
        description: 'Bytedance Seedance 2.0 Mini multi-modal.',
        featured: false,
        generationDisplayName: 'Reference to Video',
      },
    },
  },

  // ========================================
  // BYTEDANCE SEEDREAM
  // ========================================

  'bytedance/seedream': {
    displayName: 'Seedream 3.0',
    family: 'bytedance',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        pricing: 3000,
        supportedParams: ['prompt', 'image_size', 'guidance_scale', 'seed'],
        description: 'Bytedance Seedream 3.0 image generation.',
        featured: false,
      },
    },
  },

  'bytedance/seedream-v4': {
    displayName: 'Seedream 4.0',
    family: 'bytedance',
    priceUnit: 'per-image',
    variants: {
      TEXT_TO_IMAGE: {
        pricing: { image_resolution: { '1K': 3500, '2K': 5000, '4K': 7000 } },
        supportedParams: ['prompt', 'image_size', 'image_resolution', 'max_images', 'seed', 'nsfw_checker'],
        description: 'Bytedance Seedream 4.0 text-to-image. Enhanced quality with HD resolutions.',
        featured: false,
      },
      IMAGE_TO_IMAGE_EDIT: {
        pricing: { image_resolution: { '1K': 4000, '2K': 5500, '4K': 7500 } },
        supportedParams: ['prompt', 'image_urls', 'image_size', 'image_resolution', 'max_images', 'seed', 'nsfw_checker'],
        description: 'Bytedance Seedream 4.0 Edit. Image editing and transformation with up to 10 reference images.',
        featured: false,
      },
    },
  },

  'bytedance/seedream-4.5': {
    displayName: 'Seedream 4.5',
    family: 'bytedance',
    priceUnit: 'per-image',
    variants: {
      // Kie's live pricing dashboard showed ONE flat price ($0.0325/image) for this model —
      // no basic/high price split found, despite the quality param existing. Rather than
      // invent a synthetic price differential, both tiers are set to the same 3x-marked-up
      // value (verified 2026-08-26); was 4000/6000 (~123x/~185x cost). If Kie's `quality`
      // param does carry a real cost difference we didn't see, split these apart once confirmed.
      TEXT_TO_IMAGE: {
        pricing: { quality: { basic: 98, high: 98 } },
        supportedParams: ['prompt', 'aspect_ratio', 'quality', 'nsfw_checker'],
        description: 'Bytedance Seedream 4.5 text-to-image. Latest generation with quality tiers.',
        featured: false,
      },
      IMAGE_TO_IMAGE_EDIT: {
        pricing: { quality: { basic: 98, high: 98 } },
        supportedParams: ['prompt', 'image_urls', 'aspect_ratio', 'quality', 'nsfw_checker'],
        description: 'Bytedance Seedream 4.5 Edit. Latest generation image editing with up to 14 reference images.',
        featured: false,
      },
    },
  },

  'bytedance/seedream-5-lite': {
    displayName: 'Seedream 5.0 Lite',
    family: 'bytedance',
    priceUnit: 'per-image',
    variants: {
      // Same caveat as Seedream 4.5 above: Kie showed one flat price ($0.0275/image), no
      // basic/high split found. Both tiers set to the same 3x-marked-up value (was
      // 2500/4500, ~91x/~164x cost). The `ultra` (4K) tier some docs mention is still
      // unconfirmed on the live dashboard — not added here, see report §4 P1 item 13.
      TEXT_TO_IMAGE: {
        pricing: { quality: { basic: 83, high: 83 } },
        supportedParams: ['prompt', 'aspect_ratio', 'quality', 'nsfw_checker'],
        description: 'Bytedance Seedream 5.0 Lite text-to-image. Fast, efficient generation.',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        pricing: { quality: { basic: 83, high: 83 } },
        supportedParams: ['prompt', 'image_urls', 'aspect_ratio', 'quality', 'nsfw_checker'],
        description: 'Bytedance Seedream 5.0 Lite image-to-image with up to 14 reference images.',
        featured: false,
      },
    },
  },

  // ========================================
  // GROK IMAGINE
  // ========================================

  'grok/imagine': {
    displayName: 'Grok Imagine',
    family: 'grok',
    priceUnit: 'per-image',
    variants: {
      // Was 3500 (~140x Kie's real $0.025/call — the endpoint returns 4 images per call,
      // billed as one generation, consistent with how this system already prices other
      // multi-output models). Corrected 2026-08-26 to Kie cost × 2, matching Grok's own
      // confirmed video-pricing ratio (see TEXT_TO_VIDEO/IMAGE_TO_VIDEO below) rather than
      // borrowing another family's margin. IMAGE_TO_IMAGE/_EDIT below were NOT re-priced —
      // their real cost wasn't directly confirmed this session, see report §7.1.
      TEXT_TO_IMAGE: {
        pricing: 50,
        supportedParams: ['prompt', 'aspect_ratio', 'nsfw_checker', 'enable_pro'],
        description: 'Grok Imagine text-to-image generation via Kie AI.',
        featured: false,
      },
      IMAGE_TO_IMAGE: {
        pricing: 4000,
        supportedParams: ['prompt', 'image_urls', 'nsfw_checker'],
        description: 'Grok Imagine image-to-image transformation via Kie AI.',
        featured: false,
      },
      IMAGE_TO_IMAGE_EDIT: {
        pricing: 4000,
        supportedParams: ['prompt', 'image_urls', 'nsfw_checker'],
        description: 'Grok Imagine image editing via Kie AI.',
        featured: false,
      },
      TEXT_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 24, '720p': 45 },
          defaultDurationSeconds: 6,
          defaultValues: { resolution: '480p' },
        },
        supportedParams: ['prompt', 'aspect_ratio', 'duration', 'mode', 'resolution', 'nsfw_checker'],
        description: 'Grok Imagine text-to-video generation.',
        priceUnit: 'per-second-video' as PriceUnit,
        featured: false,
      },
      IMAGE_TO_VIDEO: {
        pricing: {
          kind: 'duration',
          tierParams: ['resolution'],
          ratesPerSecond: { '480p': 24, '720p': 45 },
          defaultDurationSeconds: 6,
          defaultValues: { resolution: '480p' },
        },
        supportedParams: ['prompt', 'image_urls', 'task_id', 'index', 'mode', 'aspect_ratio', 'duration', 'resolution', 'nsfw_checker', 'seed'],
        description: 'Grok Imagine image-to-video generation.',
        priceUnit: 'per-second-video' as PriceUnit,
        featured: false,
      },
      VIDEO_TO_VIDEO_UPSCALE: {
        // Was a flat 12000 (~80-120x cost). Kie's live pricing dashboard prices this by
        // resolution — the table labels its two tiers "720P → 1080P" ($0.10) and
        // "480P → 1080P" ($0.15) — reused directly as the 'resolution' tier keys, at 2x
        // margin (Grok's own confirmed ratio). Corrected 2026-08-26.
        pricing: { resolution: { '720p': 200, '480p': 300 } },
        // Chains off a prior Grok Imagine generation via task_id, not a fresh upload —
        // verified against docs.kie.ai/market/grok-imagine/upscale.md
        supportedParams: ['task_id', 'resolution'],
        description: 'Grok Imagine video upscaling.',
        priceUnit: 'per-video' as PriceUnit,
        featured: false,
      },
      VIDEO_TO_VIDEO_EXTEND: {
        // Was a flat 11000 (~50-150x cost). Kie's live dashboard prices this by
        // extend_times, with a secondary resolution-dependent cost we can't select at
        // request time (source video resolution isn't a user param on this endpoint) — used
        // the higher (720p) real cost per extend_times so we never undercharge: 6s=$0.135,
        // 10s=$0.225, both x2 margin. Corrected 2026-08-26.
        pricing: { extend_times: { '6': 270, '10': 450 } },
        // Chains off a prior Grok Imagine generation via task_id; extend_times is the
        // added segment length (6 or 10s), not the total duration — verified against
        // docs.kie.ai/market/grok-imagine/extend.md
        supportedParams: ['prompt', 'task_id', 'extend_times', 'extend_at'],
        description: 'Grok Imagine video extension.',
        priceUnit: 'per-video' as PriceUnit,
        featured: false,
      },
    },
  },

  // ========================================
  // ELEVENLABS MODELS (via Kie.ai)
  // ========================================
  // Pricing verified 2026-08-26 against kie.ai/pricing (logged-in dashboard — public docs
  // carry no $ figures). Kie credits convert at $5 = 1000 credits ($0.005/credit).

  'elevenlabs/turbo-2.5': {
    displayName: 'ElevenLabs Turbo 2.5',
    family: 'elevenlabs',
    priceUnit: 'per-1k-characters',
    variants: {
      TEXT_TO_AUDIO: {
        // 6 credits / 1000 chars = $0.03/1000 chars (kie.ai/pricing, "Elevenlabs Text to
        // Speech, turbo 2.5").
        pricing: { kind: 'text-length', ratePer1kChars: 30 },
        supportedParams: ['prompt', 'voice', 'stability', 'tts_style', 'speed'],
        description: 'ElevenLabs Turbo 2.5 text-to-speech. Fast, low-latency voice synthesis.',
        featured: true,
      },
    },
  },

  'elevenlabs/multilingual-v2': {
    displayName: 'ElevenLabs Multilingual v2',
    family: 'elevenlabs',
    priceUnit: 'per-1k-characters',
    variants: {
      TEXT_TO_AUDIO: {
        // 12 credits / 1000 chars = $0.06/1000 chars (kie.ai/pricing, "Elevenlabs Text to
        // Speech, multilingual v2").
        pricing: { kind: 'text-length', ratePer1kChars: 60 },
        supportedParams: ['prompt', 'voice'],
        description: 'ElevenLabs Multilingual v2 text-to-speech. Higher quality, 29 languages.',
        featured: false,
      },
    },
  },

  // ========================================
  // GOOGLE GEMINI TTS MODELS (via Kie.ai)
  // ========================================
  // Kie bills these per-token (input $0.7/1M, output $14/1M — kie.ai/pricing, "Gemini
  // 2.5 Pro TTS"/"Gemini 3.1 Flash TTS"), not per-character. There's no request-time
  // duration param to bill against (unlike video) — actual audio length depends on the
  // spoken text, known only after generation. We approximate a per-character rate up
  // front from a documented estimate rather than reconciling after the fact:
  //   - Speaking pace ~150 wpm, avg English word ~5.7 chars incl. space → ~70s of audio
  //     per 1000 input chars.
  //   - Gemini audio tokens ~32/sec (Gemini API's documented audio tokenization rate) →
  //     ~2240 output tokens per 1000 chars → 2240 * $14/1e6 ≈ $0.0314.
  //   - Input text tokens ~1000/4 chars-per-token → 250 tokens * $0.7/1e6 ≈ $0.0002 (negligible).
  //   Rounded up to $0.035/1000 chars to avoid undercharging if actual pacing runs slower
  //   than assumed. This is an ESTIMATE, not a Kie-confirmed rate — revisit against real
  //   task costTime/credits-consumed data once this ships.
  'google/gemini-tts-3-1-flash': {
    displayName: 'Gemini 3.1 Flash TTS',
    family: 'google',
    priceUnit: 'per-1k-characters',
    variants: {
      TEXT_TO_AUDIO: {
        pricing: { kind: 'text-length', ratePer1kChars: 35 },
        supportedParams: ['prompt', 'voice'],
        description: 'Gemini 3.1 Flash text-to-speech. Fast, natural-sounding voice synthesis.',
        featured: false,
      },
    },
  },

  'google/gemini-tts-2-5-pro': {
    displayName: 'Gemini 2.5 Pro TTS',
    family: 'google',
    priceUnit: 'per-1k-characters',
    variants: {
      TEXT_TO_AUDIO: {
        pricing: { kind: 'text-length', ratePer1kChars: 35 },
        supportedParams: ['prompt', 'voice'],
        description: 'Gemini 2.5 Pro text-to-speech. Higher-quality voice synthesis.',
        featured: false,
      },
    },
  },

}

// ========================================
// UTILITY FUNCTIONS
// ========================================

/** Type guard distinguishing DurationTieredPricing from Record<string, ParamPriceTiers> */
function isDurationTieredPricing(
  p: Record<string, ParamPriceTiers> | DurationTieredPricing | TextLengthTieredPricing,
): p is DurationTieredPricing {
  return (p as DurationTieredPricing).kind === 'duration'
}

/** Type guard distinguishing TextLengthTieredPricing from Record<string, ParamPriceTiers> */
function isTextLengthTieredPricing(
  p: Record<string, ParamPriceTiers> | DurationTieredPricing | TextLengthTieredPricing,
): p is TextLengthTieredPricing {
  return (p as TextLengthTieredPricing).kind === 'text-length'
}

/**
 * Get the valid values for a tiered param (e.g. 'resolution', 'mode') for a specific
 * model + generation type, so the UI only ever offers options that map to a real price.
 * Returns undefined if the param isn't priced (flat pricing, or not a tier dimension here).
 */
export function getTierOptions(
  model: string,
  generationType: GenerationType,
  param: CanonicalParam,
): string[] | undefined {
  const variant = getVariantConfig(model, generationType)
  if (!variant) return undefined

  const p = variant.pricing
  if (typeof p === 'number') return undefined

  if (isDurationTieredPricing(p)) {
    const index = p.tierParams.indexOf(param)
    if (index === -1) return undefined
    const values = new Set<string>()
    for (const key of Object.keys(p.ratesPerSecond)) {
      const value = key.split('|')[index]
      if (value) values.add(value)
    }
    return values.size > 0 ? Array.from(values) : undefined
  }

  if (isTextLengthTieredPricing(p)) return undefined

  return param in p ? Object.keys(p[param]) : undefined
}

/**
 * Get the allowed duration values for a model + generation type, so the UI only offers
 * durations the provider will actually accept. Returns undefined for models with a
 * continuous duration range (no discrete enum to constrain against).
 */
export function getAllowedDurations(model: string, generationType: GenerationType): string[] | undefined {
  return getVariantConfig(model, generationType)?.allowedDurations
}

/** Get variant config for a base model + generation type */
export function getVariantConfig(model: string, generationType: GenerationType): VariantConfig | undefined {
  return GENERATION_MODEL_CATALOG[model]?.variants[generationType]
}

/** Get base model config */
export function getBaseModelConfig(model: string): BaseModelConfig | undefined {
  return GENERATION_MODEL_CATALOG[model]
}

/** Get price unit for a model (falls back to base model's default) */
export function getPriceUnit(model: string, generationType?: GenerationType): PriceUnit {
  const base = GENERATION_MODEL_CATALOG[model]
  if (!base) return 'per-request'
  if (generationType) {
    const variant = base.variants[generationType]
    if (variant && 'priceUnit' in variant) return (variant as any).priceUnit as PriceUnit
  }
  return base.priceUnit
}

/**
 * Compute the effective price for a generation based on model + generation type + settings.
 */
export function computeGenerationPrice(
  model: string,
  generationType: GenerationType,
  settings?: Record<string, unknown>,
): number {
  const variant = getVariantConfig(model, generationType)
  if (!variant) return 0

  const p = variant.pricing

  // Flat pricing
  if (typeof p === 'number') return p

  // Per-character pricing that scales with prompt length (TTS — no request-time duration
  // param to bill against, so we bill against the text that will be spoken). Floored at 1
  // millidollar so a short-but-valid prompt (e.g. "Hi!") never rounds down to a $0 charge,
  // which CreateGenerationUseCase would reject as an "invalid price".
  if (isTextLengthTieredPricing(p)) {
    const chars = String(settings?.prompt ?? '').length
    if (chars === 0) return 0
    return Math.max(1, Math.round((chars / 1000) * p.ratePer1kChars))
  }

  // Per-second pricing that scales linearly with duration (real Kie rates)
  if (isDurationTieredPricing(p)) {
    const rawDuration = Number(settings?.duration)
    const duration = Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : p.defaultDurationSeconds
    // Fall back per-param to its known provider default, not to a single global default,
    // so a partially-specified request (e.g. mode set but sound omitted) still prices correctly.
    const key = p.tierParams
      .map((param) => String(settings?.[param] ?? p.defaultValues[param] ?? ''))
      .join('|')
    const rate = p.ratesPerSecond[key] ?? Math.min(...Object.values(p.ratesPerSecond))
    return Math.round(rate * duration)
  }

  // Tiered pricing — match param value to tier
  for (const [paramName, tiers] of Object.entries(p)) {
    const settingValue = settings?.[paramName]
    if (settingValue !== undefined && settingValue !== null) {
      const key = String(settingValue)
      if (tiers[key] !== undefined) return tiers[key]
    }
  }

  return computeMinGenerationPrice(model, generationType)
}

/**
 * Get the minimum possible price for a model + generation type.
 */
export function computeMinGenerationPrice(model: string, generationType: GenerationType): number {
  const variant = getVariantConfig(model, generationType)
  if (!variant) return 0

  const p = variant.pricing

  if (typeof p === 'number') return p

  // No request-time floor to compute against (unlike duration) — use the per-1000-chars
  // rate itself as the representative "starting at" price, same role defaultDurationSeconds
  // plays for duration-tiered pricing.
  if (isTextLengthTieredPricing(p)) return p.ratePer1kChars

  if (isDurationTieredPricing(p)) {
    const minRate = Math.min(...Object.values(p.ratesPerSecond))
    return Math.round(minRate * p.defaultDurationSeconds)
  }

  let min = Infinity
  for (const tiers of Object.values(p)) {
    for (const price of Object.values(tiers)) {
      if (price < min) min = price
    }
  }
  return min === Infinity ? 0 : min
}

/**
 * Get the minimum price across all generation types for a base model.
 */
export function computeBaseModelMinPrice(model: string): number {
  const base = GENERATION_MODEL_CATALOG[model]
  if (!base) return 0

  let min = Infinity
  for (const variant of Object.values(base.variants)) {
    if (!variant) continue
    const p = variant.pricing
    if (typeof p === 'number') {
      if (p < min) min = p
    } else if (isTextLengthTieredPricing(p)) {
      if (p.ratePer1kChars < min) min = p.ratePer1kChars
    } else if (isDurationTieredPricing(p)) {
      const minRate = Math.min(...Object.values(p.ratesPerSecond))
      const price = minRate * p.defaultDurationSeconds
      if (price < min) min = price
    } else {
      for (const tiers of Object.values(p)) {
        for (const price of Object.values(tiers)) {
          if (price < min) min = price
        }
      }
    }
  }
  return min === Infinity ? 0 : min
}

/**
 * Get generation types available for a base model, optionally filtered by category.
 */
export function getModelGenerationTypes(model: string, category?: GenerationCategory): GenerationType[] {
  const base = GENERATION_MODEL_CATALOG[model]
  if (!base) return []

  const types = Object.keys(base.variants) as GenerationType[]

  if (category) {
    const allowedTypes = getGenerationTypesByCategory(category)
    return types.filter(t => allowedTypes.includes(t))
  }

  return types
}

/**
 * Get all base models, optionally filtered by category.
 */
export function getAllBaseModels(category?: GenerationCategory): string[] {
  if (!category) return Object.keys(GENERATION_MODEL_CATALOG)

  const allowedTypes = getGenerationTypesByCategory(category)
  return Object.entries(GENERATION_MODEL_CATALOG)
    .filter(([_, config]) =>
      Object.keys(config.variants).some(t => allowedTypes.includes(t as GenerationType))
    )
    .map(([key]) => key)
}

// Human-readable labels for generation types
export const GENERATION_TYPE_LABELS: Record<string, string> = {
  TEXT_TO_IMAGE: 'Text to Image',
  IMAGE_TO_IMAGE: 'Image to Image',
  IMAGE_TO_IMAGE_EDIT: 'Image Edit',
  IMAGE_TO_IMAGE_UPSCALE: 'Image Upscale',
  TEXT_IMAGE_TO_IMAGE: 'Text + Image to Image',
  TEXT_TO_VIDEO: 'Text to Video',
  IMAGE_TO_VIDEO: 'Image to Video',
  IMAGE_TO_VIDEO_ANIMATE: 'Image Animation',
  TEXT_IMAGE_TO_VIDEO: 'Text + Image to Video',
  VIDEO_TO_VIDEO: 'Video to Video',
  VIDEO_TO_VIDEO_EDIT: 'Video Edit',
  VIDEO_TO_VIDEO_UPSCALE: 'Upscale',
  VIDEO_TO_VIDEO_EXTEND: 'Extend',
  TEXT_VIDEO_TO_VIDEO: 'Text + Video to Video',
  SPEECH_TO_VIDEO: 'Speech to Video',
  AUDIO_TO_VIDEO: 'Audio to Video',
  TEXT_TO_AUDIO: 'Text to Audio',
  REFERENCE_TO_IMAGE: 'Reference to Image',
  REFERENCE_TO_VIDEO: 'Reference to Video',
  IMAGE_MOTION_CONTROL: 'Image Motion Control',
  VIDEO_MOTION_CONTROL: 'Video Motion Control',
}

export default GENERATION_MODEL_CATALOG
